import { useCallback, useEffect, useState } from "react";
import { recApiClient } from "../../lib/http/recApiClient";
import { adminStorage } from "../../features/admin/service";
import { useAdminToast } from "../../context/AdminToastContext";

// Admin settings for the profile first-touch push (silent new buyers): on/off,
// the six limits, a plain-language explanation and the live cap numbers.
// Backend: /api/admin/rec-engine/profile-push/settings (buyer_intel/profile_push_settings.py).

const tok = () => {
  const t = adminStorage.getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function ago(iso?: string | null): string {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

interface ProfilePushPayload {
  settings: Record<string, any>;
  defaults: Record<string, any>;
  limits: Record<string, { min: number; max: number }>;
  updated_at: string | null;
  updated_by: string | null;
  explanation: { summary: string; steps: string[]; settings: Record<string, string> };
  live: Record<string, any>;
}

const PROFILE_PUSH_FIELDS: { key: string; label: string; unit: string }[] = [
  { key: "silence_hours", label: "Silent for", unit: "hours after onboarding" },
  { key: "min_gap_hours", label: "Gap between pushes", unit: "hours" },
  { key: "max_total", label: "Max pushes per buyer", unit: "total" },
  { key: "lookback_days", label: "Only buyers onboarded within", unit: "days" },
  { key: "batch_size", label: "Max buyers per 30-min run", unit: "buyers" },
  { key: "buyer_timeout_s", label: "Per-buyer timeout", unit: "seconds" },
];

const ProfilePushSettingsEditor = () => {
  const [data, setData] = useState<ProfilePushPayload | null>(null);
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  const apply = (d: ProfilePushPayload) => { setData(d); setDraft({ ...d.settings }); };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data: d } = await recApiClient.get<ProfilePushPayload>(
        "/api/admin/rec-engine/profile-push/settings", { headers: tok() }
      );
      apply(d);
    } catch (e: any) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || e?.message || "failed to load profile push settings";
      setLoadError(status ? `${msg} (HTTP ${status})` : `${msg} (no response from ${recApiClient.defaults.baseURL})`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch: Record<string, any>) => {
    setBusy(true);
    setError(null);
    try {
      const { data: d } = await recApiClient.put<ProfilePushPayload>(
        "/api/admin/rec-engine/profile-push/settings", patch, { headers: tok() }
      );
      apply(d);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "save failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data: d } = await recApiClient.post<ProfilePushPayload>(
        "/api/admin/rec-engine/profile-push/settings/reset", {}, { headers: tok() }
      );
      apply(d);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "reset failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="rounded-xl p-3 flex-shrink-0 border bg-gray-900 border-gray-800 text-xs text-gray-500 flex items-center justify-between gap-3">
        <span>
          {loading ? "Loading profile push settings…" : (
            <>Profile first-touch push settings could not be loaded{loadError ? `: ${loadError}` : "."}</>
          )}
        </span>
        {!loading && <button onClick={load} className="px-2 py-1 rounded bg-gray-800 text-gray-300">Retry</button>}
      </div>
    );
  }

  const enabled = !!data.settings.enabled;
  const dirty = PROFILE_PUSH_FIELDS.some(f => Number(draft[f.key]) !== Number(data.settings[f.key]));
  const live = data.live || {};

  return (
    <div className="rounded-xl p-3 flex-shrink-0 border bg-gray-900 border-gray-800">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
          Profile first-touch push (silent new buyers)
        </p>
        <button
          disabled={busy}
          onClick={() => save({ enabled: !enabled })}
          className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${
            enabled ? "bg-emerald-900/40 text-emerald-300 border-emerald-800/40" : "bg-gray-800 text-gray-400 border-gray-700"
          }`}
        >
          {enabled ? "✓ ON — click to turn off" : "OFF — click to turn on"}
        </button>
      </div>

      <p className="text-xs text-gray-400 mb-2">{data.explanation.summary}</p>
      <ol className="text-[11px] text-gray-500 space-y-1 mb-3 list-none">
        {data.explanation.steps.map((st, i) => <li key={i}>{st}</li>)}
      </ol>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        {PROFILE_PUSH_FIELDS.map(f => (
          <label key={f.key} className="text-[11px] text-gray-400 flex flex-col gap-1">
            <span title={data.explanation.settings[f.key]}>
              {f.label} <span className="text-gray-600">({f.unit}, {data.limits[f.key]?.min}–{data.limits[f.key]?.max}, default {String(data.defaults[f.key])})</span>
            </span>
            <input
              type="number"
              value={draft[f.key] ?? ""}
              min={data.limits[f.key]?.min}
              max={data.limits[f.key]?.max}
              onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
              className="bg-gray-950 border border-gray-800 rounded px-2 py-1 text-xs text-gray-200"
            />
            <span className="text-[10px] text-gray-600">{data.explanation.settings[f.key]}</span>
          </label>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button
          disabled={busy || !dirty}
          onClick={() => save(Object.fromEntries(PROFILE_PUSH_FIELDS.map(f => [f.key, Number(draft[f.key])])))}
          className="px-3 py-1 rounded bg-indigo-600 disabled:opacity-40 text-white text-xs"
        >
          Save
        </button>
        <button disabled={busy} onClick={reset} className="px-3 py-1 rounded bg-gray-800 text-gray-300 text-xs">
          Reset to defaults
        </button>
        {data.updated_at && (
          <span className="text-[10px] text-gray-600">last saved {ago(data.updated_at)}{data.updated_by ? ` by ${data.updated_by}` : ""}</span>
        )}
      </div>

      <div className="rounded-lg bg-gray-950 border border-gray-800 p-2 text-[11px] text-gray-400 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
        <span>Waiting now: <b className="text-gray-200">{live.waiting_now}{live.waiting_now_is_capped_by_batch ? "+" : ""}</b></span>
        <span>Pushed last 24h: <b className="text-gray-200">{live.buyers_pushed_last_24h}</b></span>
        <span>Pushed last 7d: <b className="text-gray-200">{live.buyers_pushed_last_7d}</b></span>
        <span>Ever pushed: <b className="text-gray-200">{live.buyers_ever_pushed}</b></span>
        <span>At lifetime cap: <b className="text-gray-200">{live.buyers_at_total_cap}</b></span>
        <span>Max per buyer: <b className="text-gray-200">1 per {data.settings.min_gap_hours}h, {live.max_pushes_per_buyer_total} total</b></span>
        <span className="col-span-2 sm:col-span-3">
          Worst case with these settings: up to <b className="text-gray-200">{live.max_pushes_per_run}</b> pushes per run,
          <b className="text-gray-200"> {live.max_pushes_per_day_upper_bound}</b> per day (48 runs). Real volume is lower: only silent buyers with preferences, minus quiet hours, mutes and the daily push budget. A run stops after {live.run_time_budget_s}s even if buyers are left; they are picked up by the next run.
        </span>
      </div>
    </div>
  );
};

export default ProfilePushSettingsEditor;
